import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Camera, Video, X, Loader2, Circle, Square } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_BYTES = 200 * 1024 * 1024; // 200MB

export default function VideoUpload({ value, onChange }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [recOpen, setRecOpen] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Video troppo grande (max 200MB)");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const ct = file.type || "video/mp4";
      const { data: init } = await axios.post(`${BACKEND}/api/upload-video/init`, {
        filename: file.name || `video-${Date.now()}.mp4`,
        content_type: ct,
      });
      const uploadId = init.upload_id;
      const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("file", blob, "chunk");
        await axios.post(`${BACKEND}/api/upload-video/chunk`, fd, { timeout: 120000 });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const { data } = await axios.post(`${BACKEND}/api/upload-video/complete`, { upload_id: uploadId });
      onChange(`${BACKEND}/api/files/${data.path}`);
      toast.success("Video caricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-3" data-testid="video-upload">
      <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="video-file-input" />
      <input ref={camRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="video-camera-input" />

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="video-upload-file-btn">
          <Upload className="mr-2 h-4 w-4" /> Carica file
        </Button>
        <Button type="button" variant="outline" onClick={() => camRef.current?.click()} disabled={uploading} data-testid="video-camera-btn">
          <Camera className="mr-2 h-4 w-4" /> Fotocamera
        </Button>
        <Button type="button" variant="outline" onClick={() => setRecOpen(true)} disabled={uploading} data-testid="video-record-btn">
          <Video className="mr-2 h-4 w-4" /> Registra (webcam)
        </Button>
        {uploading && <span className="flex items-center text-sm text-muted-foreground" data-testid="video-upload-status"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento... {progress}%</span>}
      </div>

      {uploading && (
        <Progress value={progress} className="h-2" data-testid="video-upload-progress" />
      )}

      {value && (
        <div className="rounded-xl border border-border/70 p-3" data-testid="video-preview">
          <video src={value} controls className="max-h-56 w-full rounded-lg bg-black" />
          <Button type="button" variant="ghost" size="sm" className="mt-2 text-destructive" onClick={() => onChange("")} data-testid="video-remove-btn">
            <X className="mr-1 h-3.5 w-3.5" /> Rimuovi video
          </Button>
        </div>
      )}

      <Recorder open={recOpen} onClose={() => setRecOpen(false)} onRecorded={(blob) => { setRecOpen(false); doUpload(new File([blob], `rec-${Date.now()}.webm`, { type: "video/webm" })); }} />
    </div>
  );
}

function Recorder({ open, onClose, onRecorded }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [ready, setReady] = useState(false);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      setReady(true);
    } catch (e) {
      toast.error("Impossibile accedere alla fotocamera");
      onClose();
    }
  };

  const startRec = () => {
    chunksRef.current = [];
    const mr = new MediaRecorder(streamRef.current);
    mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      cleanup();
      onRecorded(blob);
    };
    recorderRef.current = mr;
    mr.start();
    setRecording(true);
  };

  const stopRec = () => { recorderRef.current?.stop(); setRecording(false); };

  const cleanup = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
    setRecording(false);
  };

  const handleOpenChange = (o) => {
    if (o) start();
    else { cleanup(); onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Registra video</DialogTitle></DialogHeader>
        <video ref={videoRef} muted playsInline className="aspect-video w-full rounded-lg bg-black" />
        <div className="flex justify-center gap-2">
          {!recording ? (
            <Button type="button" onClick={startRec} disabled={!ready} data-testid="rec-start-btn"><Circle className="mr-2 h-4 w-4 fill-current text-red-500" /> Avvia registrazione</Button>
          ) : (
            <Button type="button" variant="destructive" onClick={stopRec} data-testid="rec-stop-btn"><Square className="mr-2 h-4 w-4" /> Ferma e usa</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
